// React
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
// MUI
import { Grid, Box, Button, Container, Divider } from "@mui/material";
import Iconify from "../../../components/iconify/Iconify";
// Constants
import EntityConstants from "../../../configs/EntityConstants";
import MessageContants from "../../../configs/MessageContants";
// Service
import { get, post, put, gen_link } from "../../../services/FetchHelper";
import SpinService from "../../../services/SpinService";
// Utils
import { CREDIT_NOTE_STATE, CREDIT_NOTE_SCHEMA, CREDIT_NOTE_FORM_FIELDS, encodeCreditTarget, decodeCreditTarget } from "./CreditNote.utils";
// Styles
import { StyledDrawerForm } from "../../../configs/GoStyles";
//Third party
import { Form, Formik } from "formik";
import toast from "react-hot-toast";
import GoForm from "../../../components/Inputs/GoForm";
import PermissionConstants from "../../../configs/PermissionConstants";
import usePermissions from "../../../hooks/usePermissions";

import CreditNoteLines from "./CreditNoteLines";

const CreditNote = ({ id, onClose }) => {
    const permissions = usePermissions();
    const spinService = SpinService();
    const [creditNote, setCreditNote] = useState(CREDIT_NOTE_STATE);
    const [schema, setSchema] = useState(CREDIT_NOTE_SCHEMA);
    const [formFields, setFormFields] = useState(CREDIT_NOTE_FORM_FIELDS);
    const [creditNoteLines, setCreditNoteLines] = useState([]);
    const [creditNoteId, setCreditNoteId] = useState(id);

    const attachOptions = (name, options) => {
        setFormFields(prevFormFields =>
            prevFormFields.map(field =>
                field.name === name && (field.type === "select" || field.type === "autocomplete")
                    ? { ...field, options: options }
                    : field
            )
        );
    };

    const loadCreditTargetOptions = async () => {
        spinService.on();
        const [studentsRes, accountsRes] = await Promise.all([
            get(EntityConstants.STUDENTS, "max=all"),
            get(EntityConstants.ACCOUNTS, "max=all")
        ]);
        const students = await studentsRes.json();
        const accounts = await accountsRes.json();
        const merged = [
            ...students.map(s => ({ label: `Student: ${s.code_name || `${s.first_name} - ${s.code}`}`, value: `student:${s.id}` })),
            ...accounts.map(a => ({ label: `Account: ${a.name}`, value: `account:${a.id}` }))
        ];
        attachOptions("credit_target", merged);
        spinService.off();
    };

    const handleCreditNoteChange = async (creditNoteId) => {
        spinService.on();
        const response = await get(EntityConstants.CREDIT_NOTES + `/${creditNoteId}`);
        const data = await response.json();
        setCreditNote({ ...data, credit_target: encodeCreditTarget(data) });
        spinService.off();
        setCreditNoteLines(data.credit_note_lines);
    };

    const handleValueChange = (e, values) => {
        setCreditNote((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSubmit = async (values) => {
        spinService.on();
        const { credit_ledger_id, student_id } = decodeCreditTarget(values.credit_target);
        const payload = { ...values, credit_ledger_id, student_id };
        delete payload.credit_target;
        const isNewCreditNote = values.id === undefined;
        const url = isNewCreditNote ? EntityConstants.CREDIT_NOTES : EntityConstants.CREDIT_NOTES + `/${values.id}`;
        const method = isNewCreditNote ? post : put;
        const response = await method(url, { credit_note: payload });
        if ([200, 201].indexOf(response.status) > -1) {
            const data = await response.json();
            toast.success(isNewCreditNote ? MessageContants.CREDIT_NOTE_CREATED : MessageContants.CREDIT_NOTE_UPDATED);
            handleCreditNoteChange(data.id);
            setCreditNoteId(data.id);
            setCreditNoteLines(data.credit_note_lines);
        }
        spinService.off();
    };

    const handlePdfDownloadChange = async () => {
        const response = await fetch(gen_link(`${EntityConstants.CREDIT_NOTE_PDF_DOWNLOAD}/${creditNoteId}`));
        const pdfBlob = await response.blob();
        const pdfBlobUrl = URL.createObjectURL(pdfBlob);
        const previewWindow = window.open(pdfBlobUrl, '_blank');

        // const link = document.createElement('a');
        // link.href = gen_link(`${EntityConstants.CREDIT_NOTE_PDF_DOWNLOAD}/${creditNoteId}`);
        // link.target = '_blank';
        // link.click();
    };

    useEffect(() => {
        loadCreditTargetOptions();
        if (id !== undefined) {
            setCreditNoteId(Number(id))
            handleCreditNoteChange(Number(id));
        }
        return () => {
            setCreditNote({});
        }
    }, [id]);

    return (
        <Container>
            <Formik
                enableReinitialize
                initialValues={creditNote}
                validationSchema={schema}
                onSubmit={(values) => handleSubmit(values)}
            >
                {({
                    values,
                    touched,
                    errors,
                    handleChange,
                    handleSubmit,
                }) => (
                    <Form noValidate onSubmit={handleSubmit}>
                        <StyledDrawerForm>
                            <Grid
                                container
                                // maxWidth="md"
                                rowSpacing={1}
                                columnSpacing={{ xs: 1, sm: 2, md: 2 }}
                            >
                                {formFields.map((formField, index) => {
                                    return (
                                        <Grid key={index} item xs={12} sm={12} md={6} lg={6}>
                                            <GoForm
                                                config={formField}
                                                values={values}
                                                touched={touched}
                                                errors={errors}
                                                handleChange={(e) => { handleChange(e); handleValueChange(e, values); }}
                                            />
                                        </Grid>
                                    );
                                })}
                            </Grid>
                            <Box sx={{ p: 2, textAlign: "right" }}>
                                <Button
                                    variant="contained"
                                    color="error"
                                    startIcon={<Iconify icon="eva:close-circle-outline" />}
                                    onClick={() => onClose()}
                                >
                                    Close
                                </Button>
                                &nbsp;&nbsp;
                                {((values.id === undefined && permissions.has(PermissionConstants.CREDIT_NOTE_CREATE)) || (values.id !== undefined && permissions.has(PermissionConstants.CREDIT_NOTE_UPDATE))) &&
                                    <Button
                                        variant="contained"
                                        startIcon={<Iconify icon="eva:save-fill" />}
                                        type="submit"
                                    >
                                        {values.id === undefined ? 'Submit' : 'Update'}
                                    </Button>
                                }
                                &nbsp;&nbsp;
                                {(values.id !== undefined && permissions.has(PermissionConstants.CREDIT_NOTE_DOWNLOAD)) &&
                                    <Button
                                        variant="contained"
                                        startIcon={<Iconify icon="typcn:download" />}
                                        onClick={handlePdfDownloadChange}
                                    >
                                        Download
                                    </Button>
                                }
                            </Box>
                        </StyledDrawerForm>
                    </Form>
                )}
            </Formik>
            <Divider />
            <br />
            {creditNoteId !== undefined && <CreditNoteLines creditNoteId={creditNoteId} creditNoteLines={creditNoteLines} reload={() => handleCreditNoteChange(creditNoteId)} />}
        </Container>
    );
};

CreditNote.propTypes = {
    id: PropTypes.number,
    onClose: PropTypes.any,
};

export default CreditNote;